import React, { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';

// Critical pages - loaded eagerly (public, SEO-important)
import Landing from "@/pages/Landing";
import ChaptersIndex from "@/pages/ChaptersIndex";
import ChapterDetail from "@/pages/ChapterDetail";
import VersePage from "@/pages/VersePage";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";

// Non-critical pages - lazy loaded
const QuotesPage = lazy(() => import("@/pages/QuotesPage"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const TriggerScreen = lazy(() => import("@/pages/TriggerScreen"));
const AIGuide = lazy(() => import("@/pages/AIGuide"));
const GunaDashboard = lazy(() => import("@/pages/GunaDashboard"));
const GunaRetakeQuiz = lazy(() => import("@/pages/GunaRetakeQuiz"));
const DaiviAsuriQuiz = lazy(() => import("@/pages/DaiviAsuriQuiz"));
const DoshaQuiz = lazy(() => import("@/pages/DoshaQuiz"));
const KoshaPage = lazy(() => import("@/pages/KoshaPage"));
const WellnessJourney = lazy(() => import("@/pages/WellnessJourney"));
const Reflection = lazy(() => import("@/pages/Reflection"));
const Programs = lazy(() => import("@/pages/Programs"));
const Lifestyle = lazy(() => import("@/pages/Lifestyle"));
const Family = lazy(() => import("@/pages/Family"));
const Profile = lazy(() => import("@/pages/Profile"));
const Journey = lazy(() => import("@/pages/Journey"));
const ShareTransformation = lazy(() => import("@/pages/ShareTransformation"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Subscribe = lazy(() => import("@/pages/Subscribe"));
const SupportUs = lazy(() => import("@/pages/SupportUs"));
const EmailPreferences = lazy(() => import("@/pages/EmailPreferences"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));

import AppLayout from "@/components/AppLayout";
import CookieConsent from "@/components/CookieConsent";

const ProtectedRoute = ({ children, adminOnly = false, allowWithoutOnboarding = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="loading-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-light">Loading your journey...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'superadmin') return <Navigate to="/dashboard" replace />;
  // Dashboard is accessible without onboarding (shows blocking modal)
  // All other pages require onboarding completion
  if (!allowWithoutOnboarding && !user.onboarding_complete && user.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Allow access even if coming from dashboard (onboarding_complete might be false)
  if (user.onboarding_complete) return <Navigate to="/dashboard" replace />;
  return children;
};

const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Share Target fallback — when SW doesn't intercept, redirect to AI Guide with shared text
const ShareTargetRedirect = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  React.useEffect(() => {
    const title = params.get('title') || '';
    const text = params.get('text') || '';
    const url = params.get('url') || '';
    const shared = [title, text, url].filter(Boolean).join(' — ');
    navigate(`/ai-guide?shared=${encodeURIComponent(shared)}`, { replace: true });
  }, [params, navigate]);
  return null;
};

const AppRouter = () => {
  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/quotes" element={<QuotesPage />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/chapters" element={<ChaptersIndex />} />
        <Route path="/chapter/:id" element={<ChapterDetail />} />
        <Route path="/verse/:chapter/:verse" element={<VersePage />} />
        <Route path="/:lang/chapters" element={<ChaptersIndex />} />
        <Route path="/:lang/chapter/:id" element={<ChapterDetail />} />
        <Route path="/:lang/verse/:chapter/:verse" element={<VersePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/google/callback" element={<AuthCallback />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<Login />} />
        <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute allowWithoutOnboarding><Dashboard /></ProtectedRoute>} />
        <Route path="/trigger" element={<ProtectedRoute><TriggerScreen /></ProtectedRoute>} />
        <Route path="/ai-guide" element={<ProtectedRoute><AIGuide /></ProtectedRoute>} />
        <Route path="/share-target" element={<ShareTargetRedirect />} />
        <Route path="/guna" element={<ProtectedRoute><GunaDashboard /></ProtectedRoute>} />
        <Route path="/guna/retake" element={<ProtectedRoute><GunaRetakeQuiz /></ProtectedRoute>} />
        <Route path="/guna/daivi-asuri" element={<ProtectedRoute><DaiviAsuriQuiz /></ProtectedRoute>} />
        <Route path="/dosha-quiz" element={<ProtectedRoute><DoshaQuiz /></ProtectedRoute>} />
        <Route path="/kosha" element={<ProtectedRoute><KoshaPage /></ProtectedRoute>} />
        <Route path="/wellness-journey" element={<ProtectedRoute><WellnessJourney /></ProtectedRoute>} />
        <Route path="/reflection" element={<ProtectedRoute><Reflection /></ProtectedRoute>} />
        <Route path="/programs" element={<ProtectedRoute><Programs /></ProtectedRoute>} />
        <Route path="/lifestyle" element={<ProtectedRoute><Lifestyle /></ProtectedRoute>} />
        <Route path="/family" element={<ProtectedRoute><Family /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/journey" element={<ProtectedRoute><Journey /></ProtectedRoute>} />
        <Route path="/share" element={<ProtectedRoute><ShareTransformation /></ProtectedRoute>} />
        <Route path="/subscribe" element={<ProtectedRoute allowWithoutOnboarding><Subscribe /></ProtectedRoute>} />
        <Route path="/support-us" element={<ProtectedRoute allowWithoutOnboarding><SupportUs /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        
        {/* Email Management Routes */}
        <Route path="/email-preferences" element={<ProtectedRoute allowWithoutOnboarding><EmailPreferences /></ProtectedRoute>} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  const { i18n } = useTranslation();
  const langCode = (i18n.language || 'en').split('-')[0].split('@')[0];

  return (
    <HelmetProvider>
      <Helmet>
        <html lang={langCode} />
      </Helmet>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AppRouter />
          <CookieConsent />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
